import { sum } from '../../../../../helpers/array'
import { ceilingToPrecision, roundToPrecision } from '../../../../../helpers/math'
import type { HourlyEmployee, SalaryEmployee, Employees, LaborStatistics } from '../types'

const roundToNearestCent = (dollars: number): number => roundToPrecision(dollars, 2)

export const calculateTotalHourlyCompensation = (employee: HourlyEmployee): number => {
  const regularHours = employee.hoursWorkedPerYear - employee.overtimeHours
  return roundToNearestCent(regularHours * employee.hourlyWage +
    employee.overtimeHours * employee.hourlyWage * employee.overtimeMultiplier +
    employee.bonuses)
}

export const calculateTotalSalaryCompensation = (employee: SalaryEmployee): number =>
  employee.annualSalary + employee.bonuses

const calculateTotalAnnualCostOfLabor = (employees: Employees): number => {
  return sum(employees.salary, x => calculateTotalSalaryCompensation(x)) +
    sum(employees.hourly, x => calculateTotalHourlyCompensation(x))
}

const calculateTotalAnnualHours = (employees: Employees): number => {
  return sum([...employees.salary, ...employees.hourly], x => x.hoursWorkedPerYear)
}

const calculateTotalUnbillableHours = (employees: Employees): number => {
  return sum([...employees.salary, ...employees.hourly], x => x.unbillableHoursPerYear)
}

const calculatePercentageOfUnbillableHours = (unbillableHours: number, annualHours: number): number => {
  if (annualHours === 0) {
    return 0
  }

  return roundToPrecision(unbillableHours / annualHours, 4)
}

const calculateManHourCost = (totalCost: number, hours: number): number => {
  if (hours === 0) {
    return 0
  }
  return ceilingToPrecision(totalCost / hours, 2)
}

const calculateTotalUnbillableCost = (employees: Employees) => {
  const hourlyUnbillable = sum(employees.hourly, x => x.unbillableHoursPerYear * x.hourlyWage + x.bonuses)
  const salaryCost = (employee: SalaryEmployee): number => employee.hoursWorkedPerYear === 0
    ? employee.annualSalary
    : (employee.annualSalary / employee.hoursWorkedPerYear) * employee.unbillableHoursPerYear
  const salaryUnbillable = sum(employees.salary, x => salaryCost(x) + x.bonuses)
  return hourlyUnbillable + salaryUnbillable
}

const calculateTotalOvertimeHours = (employees: Employees): number => {
  return sum([...employees.salary, ...employees.hourly], x => x.overtimeHours)
}

const calculateOvertimeFactor = (employees: Employees, annualHours: number): number => {
  if (annualHours === 0) {
    return 0
  }

  return roundToPrecision(calculateTotalOvertimeHours(employees) / annualHours, 4)
}

export const calculateLaborStatistics = (employees: Employees): LaborStatistics => {
  const annualCostOfLabor = calculateTotalAnnualCostOfLabor(employees)
  const annualHours = calculateTotalAnnualHours(employees)
  const unbillableHours = calculateTotalUnbillableHours(employees)
  const unbillableCost = calculateTotalUnbillableCost(employees)
  return {
    annualCostOfLabor,
    annualHours,
    billableHours: annualHours - unbillableHours,
    billableCost: annualCostOfLabor - unbillableCost,
    unbillableHours,
    unbillablePercent: calculatePercentageOfUnbillableHours(unbillableHours, annualHours),
    manHourCost: calculateManHourCost(annualCostOfLabor - unbillableCost, annualHours - unbillableHours),
    overtimeFactor: calculateOvertimeFactor(employees, annualHours),
    unbillableCost
  }
}
